import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Trophy, TrendingUp, Video, Music, Users, Play, MapPin, Sparkles, Clock, Info, ChevronRight, Star, Mic, Radio, Camera, Palette, Headphones, Building, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { TierBadge, TierLegend } from '../components/TierBadge';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { api } from '../utils/api';

// Creator role chart configurations
const CREATOR_CHART_TABS = [
  { id: 'podcaster', name: 'Podcasters', icon: Mic, endpoint: '/charts/top-20-podcaster' },
  { id: 'streamer', name: 'Streamers', icon: Radio, endpoint: '/charts/top-20-streamer' },
  { id: 'videographer', name: 'Videographers', icon: Camera, endpoint: '/charts/top-20-videographer' },
  { id: 'studio', name: 'Studios', icon: Building, endpoint: '/charts/top-20-studio' },
  { id: 'graphic_designer', name: 'Designers', icon: Palette, endpoint: '/charts/top-20-graphic_designer' },
  { id: 'producer', name: 'Producers', icon: Headphones, endpoint: '/charts/top-20-producer' },
];



export default function ChartsPage() {
  const [activeTab, setActiveTab] = useState('trending');
  const [artistCharts, setArtistCharts] = useState([]);
  const [videoCharts, setVideoCharts] = useState([]);
  const [genreCharts, setGenreCharts] = useState([]);
  const [regionalCharts, setRegionalCharts] = useState([]);
  const [risingArtists, setRisingArtists] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [creatorCharts, setCreatorCharts] = useState({});
  const [trendingData, setTrendingData] = useState(null);
  const [trendingNow, setTrendingNow] = useState([]);
  const [risingFast, setRisingFast] = useState([]);
  const [newBuzzing, setNewBuzzing] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [genreData, setGenreData] = useState(null);
  const [regionData, setRegionData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    fetchInitialData();
    fetchTrendingData();
  }, []);

  useEffect(() => {
    if (selectedGenre) {
      fetchGenreChart(selectedGenre);
    }
  }, [selectedGenre]);

  useEffect(() => {
    if (selectedRegion) {
      fetchRegionChart(selectedRegion);
    }
  }, [selectedRegion]);

  const fetchInitialData = async () => {
    try {
      const [artistsResult, videosResult, genresResult, regionsResult, metadataResult] = await Promise.all([
        api.get('/charts/top-20-artists'),
        api.get('/charts/top-20-videos'),
        api.get('/charts/genres'),
        api.get('/charts/regions'),
        api.get('/charts/metadata')
      ]);

      if (artistsResult.ok) setArtistCharts(artistsResult.data);
      if (videosResult.ok) setVideoCharts(videosResult.data);
      if (genresResult.ok) {
        setGenreCharts(genresResult.data?.charts || []);
      }
      if (regionsResult.ok) {
        setRegionalCharts(regionsResult.data?.charts || []);
      }
      if (metadataResult.ok) setMetadata(metadataResult.data);
    } catch (error) {
      console.error('Failed to fetch charts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendingData = async () => {
    try {
      const [chartsRes, nowRes, risingRes, buzzRes] = await Promise.all([
        api.get('/charts', { period: '7d' }),
        api.get('/trending/now', { limit: 10 }),
        api.get('/trending/rising', { limit: 10 }),
        api.get('/trending/new-buzzing', { limit: 10 }),
      ]);
      if (chartsRes.ok) setTrendingData(chartsRes.data);
      if (nowRes.ok) setTrendingNow(nowRes.data?.trending_now || []);
      if (risingRes.ok) setRisingFast(risingRes.data?.rising_fast || []);
      if (buzzRes.ok) setNewBuzzing(buzzRes.data?.new_buzzing || []);
    } catch (error) {
      console.error('Failed to fetch trending data:', error);
    }
  };

  const fetchGenreChart = async (genreId) => {
    try {
      const { ok, data } = await api.get(`/charts/genre/${genreId}`);
      if (ok) {
        setGenreData(data);
      }
    } catch (error) {
      console.error('Failed to fetch genre chart:', error);
    }
  };

  const fetchRegionChart = async (regionId) => {
    try {
      const { ok, data } = await api.get(`/charts/region/${regionId}`);
      if (ok) {
        setRegionData(data);
      }
    } catch (error) {
      console.error('Failed to fetch region chart:', error);
    }
  };

  const fetchRisingArtists = async () => {
    try {
      const { ok, data } = await api.get('/charts/rising');
      if (ok) {
        setRisingArtists(data?.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch rising artists:', error);
    }
  };

  const fetchNewReleases = async () => {
    try {
      const { ok, data } = await api.get('/charts/new-releases');
      if (ok) {
        setNewReleases(data?.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch new releases:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'rising') {
      fetchRisingArtists();
    } else if (activeTab === 'new-releases') {
      fetchNewReleases();
    } else if (CREATOR_CHART_TABS.find(t => t.id === activeTab)) {
      fetchCreatorChart(activeTab);
    }
  }, [activeTab]);

  const fetchCreatorChart = async (roleId) => {
    try {
      const tab = CREATOR_CHART_TABS.find(t => t.id === roleId);
      if (!tab) return;
      
      const { ok, data } = await api.get(tab.endpoint);
      if (ok) {
        setCreatorCharts(prev => ({ ...prev, [roleId]: data }));
      }
    } catch (error) {
      console.error(`Failed to fetch ${roleId} chart:`, error);
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'text-4xl text-secondary';
    if (rank === 2) return 'text-3xl text-muted-foreground';
    if (rank === 3) return 'text-2xl text-amber-700';
    return 'text-xl text-muted-foreground';
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-secondary">1st</Badge>;
    if (rank === 2) return <Badge className="bg-muted-foreground text-background">2nd</Badge>;
    if (rank === 3) return <Badge className="bg-amber-700">3rd</Badge>;
    return null;
  };

  const ArtistChartCard = ({ entry, index, showBadges = true }) => (
    <motion.div
      key={entry.artist_id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
    >
      <Link to={`/artist/${entry.artist_id}`}>
        <Card className={`glass-card border-border p-4 sm:p-6 hover-lift ${entry.rank <= 3 ? 'neon-border' : ''}`}>
          <div className="flex items-center gap-4 sm:gap-6">
            {/* Rank */}
            <div className="w-12 sm:w-16 text-center">
              <span className={`font-heading font-black ${getRankStyle(entry.rank)}`}>
                {entry.rank}
              </span>
            </div>

            {/* Avatar */}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
              {entry.avatar_url ? (
                <img src={entry.avatar_url} alt={entry.artist_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <span className="text-2xl font-heading font-bold">{entry.artist_name[0]}</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase truncate">
                  {entry.artist_name}
                </h3>
                {/* Tier Badge */}
                <TierBadge 
                  tier={entry.tier}
                  subscriptionStatus={entry.subscription_status}
                  isFounder={entry.is_founder}
                  size="sm"
                />
                {getRankBadge(entry.rank)}
              </div>
              
              {/* Chart Badges */}
              {showBadges && entry.chart_badges && entry.chart_badges.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {entry.chart_badges.map((badge, i) => (
                    <Badge key={i} variant="outline" className="text-xs bg-primary/10 border-primary/30 text-primary">
                      {badge}
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4 text-secondary" />
                  {entry.total_requests} requests
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {entry.unique_requesters} unique
                </span>
                <span className="flex items-center gap-1">
                  <Play className="w-4 h-4" />
                  {entry.total_plays?.toLocaleString() || 0} plays
                </span>
                {entry.genre && (
                  <span className="flex items-center gap-1">
                    <Music className="w-4 h-4" />
                    {entry.genre}
                  </span>
                )}
                {entry.region && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {entry.region}
                  </span>
                )}
              </div>
            </div>

            {/* Change indicator */}
            {entry.change !== 0 && (
              <div className={`text-sm font-bold ${entry.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {entry.change > 0 ? `+${entry.change}` : entry.change}
              </div>
            )}
          </div>
        </Card>
      </Link>
    </motion.div>
  );

  const VideoChartCard = ({ entry, index }) => (
    <motion.div
      key={entry.video_id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
    >
      <Card className={`glass-card border-border p-4 sm:p-6 hover-lift ${entry.rank <= 3 ? 'neon-orange' : ''}`}>
        <div className="flex items-center gap-4 sm:gap-6">
          {/* Rank */}
          <div className="w-12 sm:w-16 text-center">
            <span className={`font-heading font-black ${getRankStyle(entry.rank)}`}>
              {entry.rank}
            </span>
          </div>

          {/* Thumbnail */}
          <div className="w-24 h-14 sm:w-32 sm:h-18 rounded overflow-hidden flex-shrink-0 relative">
            {entry.thumbnail_url ? (
              <img src={entry.thumbnail_url} alt={entry.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                <Video className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <Play className="w-8 h-8 text-white" fill="white" />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading text-lg sm:text-xl font-bold uppercase truncate">
                {entry.title}
              </h3>
              {getRankBadge(entry.rank)}
            </div>
            <p className="text-sm text-muted-foreground mb-1">{entry.artist_name}</p>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-secondary" />
                {entry.total_requests} requests
              </span>
              <span className="flex items-center gap-1">
                <Play className="w-4 h-4" />
                {entry.total_plays?.toLocaleString() || 0} views
              </span>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );

  const EmptyState = ({ icon: Icon, title, description, color = "primary" }) => (
    <StageEmptyState 
      variant="charts"
      customHeadline={title}
      customHint={description}
    />
  );

  // Creator Chart Card for role-based charts
  const CreatorChartCard = ({ entry, index, role }) => {
    const Icon = CREATOR_CHART_TABS.find(t => t.id === role)?.icon || Users;
    
    return (
      <motion.div
        key={entry.user_id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.03 }}
      >
        <Link to={`/profile/${entry.handle || entry.user_id}`}>
          <Card className={`glass-card border-border p-4 sm:p-6 hover-lift ${entry.position <= 3 ? 'neon-border' : ''}`}>
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Rank */}
              <div className="w-12 sm:w-16 text-center">
                <span className={`font-heading font-black ${getRankStyle(entry.position)}`}>
                  {entry.position}
                </span>
              </div>

              {/* Avatar */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                {entry.avatar_url ? (
                  <img src={entry.avatar_url} alt={entry.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase truncate">
                    {entry.name}
                  </h3>
                  {entry.badge && <span className="text-2xl">{entry.badge}</span>}
                </div>
                
                {entry.handle && (
                  <p className="text-sm text-muted-foreground mb-1">{entry.handle ? `@${entry.handle}` : ""}</p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-secondary" />
                    {entry.stats?.requests || 0} requests
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {entry.stats?.followers?.toLocaleString() || 0} followers
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    Score: {entry.score}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </Link>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 sm:py-8 px-4 sm:px-6 lg:px-8 pb-24" data-testid="charts-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-6 sm:mb-8">
          <Badge className="mb-3 sm:mb-4 bg-secondary/20 text-secondary">
            <TrendingUp className="w-3 h-3 mr-1" />
            Updated Weekly
          </Badge>
          <h1 className="font-heading text-[clamp(1.75rem,5vw,3.75rem)] font-black uppercase mb-2 sm:mb-4 leading-tight">
            DMV <span className="text-secondary">Charts</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-3 sm:mb-4 leading-relaxed px-2">
            Ranked by listener requests. Monday to Sunday. The voice of the DMV.
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRules(!showRules)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Info className="w-4 h-4 mr-2" />
            How Charts Work
          </Button>
        </div>

        {/* Chart Rules Panel */}
        {showRules && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="glass-card border-secondary/20 p-6 mb-8 max-w-4xl mx-auto">
              <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-secondary" />
                How DMV Soundstage Charts Work
              </h3>
              
              <div className="bg-muted/30 rounded-lg p-4 mb-6 text-sm">
                <p className="italic text-foreground">
                  "Charts on DMV Soundstage are powered by <strong>real listener requests</strong> — not streams, bots, or paid placement."
                </p>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Each time a listener requests your song, it counts toward:
              </p>
              <ul className="mb-4 space-y-1 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-secondary" /> Your Genre Chart
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-secondary" /> The Top 20 DMV Artists (Overall)
                </li>
              </ul>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold mb-2">How Rankings Work</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Total valid requests</li>
                    <li>Number of unique listeners</li>
                    <li>Plays during the week</li>
                    <li>Most recent activity</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Anti-Spam Rules</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• 1 request per track per user per 24 hours</li>
                    <li>• Maximum 20 requests per user per day</li>
                    <li>• Banned accounts' requests don't count</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="font-bold mb-2 text-amber-400">Important</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Boosts increase visibility, NOT chart position</strong></li>
                  <li>• <strong>No pay-to-win rankings</strong></li>
                  <li>• Fraudulent activity is removed</li>
                  <li>• Repeat abuse results in penalties</li>
                </ul>
                <p className="mt-3 text-sm font-medium text-secondary">
                  This system rewards real fan engagement.
                </p>
              </div>
              
              <div className="mt-4 text-xs text-muted-foreground">
                Charts reset every week (Monday 12:00 AM – Sunday 11:59 PM ET)
              </div>
            </Card>
          </motion.div>
        )}

        {/* Tier Legend */}
        <TierLegend className="mb-6 sm:mb-8 max-w-4xl mx-auto" />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile-friendly horizontal scroll tabs */}
          <div className="relative mb-6 sm:mb-8">
            <div className="overflow-hidden">
            <div className="overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" style={{ overscrollBehaviorX: 'contain', WebkitOverflowScrolling: 'touch' }}>
              <TabsList className="bg-muted/50 inline-flex w-max min-w-full sm:min-w-0 sm:w-auto gap-1 p-1">
                <TabsTrigger value="trending" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="trending-chart-tab">
                  <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Trending</span>
                </TabsTrigger>
                <TabsTrigger value="overall" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="overall-chart-tab">
                  <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Top 20</span>
                </TabsTrigger>
                <TabsTrigger value="videos" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="videos-chart-tab">
                  <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Videos</span>
                </TabsTrigger>
                <TabsTrigger value="genres" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="genres-chart-tab">
                  <Music className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Genres</span>
                </TabsTrigger>
                <TabsTrigger value="regions" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="regions-chart-tab">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Regions</span>
                </TabsTrigger>
                <TabsTrigger value="rising" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="rising-chart-tab">
                  <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>Rising</span>
                </TabsTrigger>
                <TabsTrigger value="new-releases" className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="new-releases-tab">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>New</span>
                </TabsTrigger>
                {/* Creator Role Tabs - separated by divider on desktop */}
                <div className="hidden sm:block w-px h-6 bg-muted/50 mx-1 self-center" />
                {CREATOR_CHART_TABS.map(tab => (
                  <TabsTrigger key={tab.id} value={tab.id} className="px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid={`${tab.id}-chart-tab`}>
                    <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                    <span>{tab.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            </div>
            {/* Scroll hint gradient - mobile only */}
            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none sm:hidden" />
          </div>

          {/* Trending - Auto-Stats Engine */}
          <TabsContent value="trending">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-orange-500/20 text-orange-400">
                  <Flame className="w-3 h-3 mr-1" />
                  Engagement Based
                </Badge>
              </div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold uppercase mb-1 sm:mb-2">
                Trending This Week
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Ranked by engagement score — plays, likes, comments, shares, and saves
              </p>
            </div>

            {trendingData ? (
              <div className="space-y-10">
                {/* Trending Tracks */}
                {trendingData.top_tracks?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                      <Music className="w-5 h-5 text-secondary" />
                      Trending Tracks
                    </h3>
                    <div className="space-y-3" data-testid="trending-tracks">
                      {trendingData.top_tracks.map((entry, index) => (
                        <motion.div
                          key={entry.target_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Link to={entry.link_url || `/track/${entry.target_id}`}>
                            <Card className={`glass-card border-border p-4 hover-lift ${index < 3 ? 'neon-border' : ''}`}>
                              <div className="flex items-center gap-4">
                                <div className={`w-10 sm:w-12 text-center font-heading font-black ${
                                  index === 0 ? 'text-2xl text-secondary' :
                                  index === 1 ? 'text-xl text-muted-foreground' :
                                  index === 2 ? 'text-xl text-amber-600' : 'text-lg text-muted-foreground'
                                }`}>
                                  {entry.rank}
                                </div>
                                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-orange-900/50 to-amber-600/30">
                                  {entry.cover_url ? (
                                    <img src={entry.cover_url} alt={entry.title} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Music className="w-6 h-6 text-secondary/50" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-heading font-bold uppercase truncate">
                                    {entry.title || entry.target_id}
                                  </h4>
                                  {entry.handle && (
                                    <p className="text-xs text-muted-foreground">{entry.handle ? `@${entry.handle}` : ""}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
                                  <span className="flex items-center gap-1" title="Plays">
                                    <Play className="w-3 h-3" /> {entry.stats?.plays || 0}
                                  </span>
                                  <span className="flex items-center gap-1 hidden sm:flex" title="Likes">
                                    <Star className="w-3 h-3" /> {entry.stats?.likes || 0}
                                  </span>
                                  <span className="flex items-center gap-1 hidden sm:flex" title="Shares">
                                    <Users className="w-3 h-3" /> {entry.stats?.shares || 0}
                                  </span>
                                  <Badge variant="outline" className="text-xs text-secondary border-secondary/30">
                                    {entry.score?.toFixed(1)}
                                  </Badge>
                                </div>
                              </div>
                            </Card>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending Videos */}
                {trendingData.top_videos?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                      <Video className="w-5 h-5 text-red-400" />
                      Trending Videos
                    </h3>
                    <div className="space-y-3" data-testid="trending-videos">
                      {trendingData.top_videos.map((entry, index) => (
                        <motion.div
                          key={entry.target_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Link to={entry.link_url || `/video/${entry.target_id}`}>
                            <Card className={`glass-card border-border p-4 hover-lift ${index < 3 ? 'neon-orange' : ''}`}>
                              <div className="flex items-center gap-4">
                                <div className={`w-10 sm:w-12 text-center font-heading font-black ${
                                  index === 0 ? 'text-2xl text-red-400' :
                                  index === 1 ? 'text-xl text-muted-foreground' :
                                  index === 2 ? 'text-xl text-amber-600' : 'text-lg text-muted-foreground'
                                }`}>
                                  {entry.rank}
                                </div>
                                <div className="w-20 h-12 sm:w-24 sm:h-14 rounded overflow-hidden flex-shrink-0 bg-gradient-to-br from-red-900/50 to-orange-600/30 relative">
                                  {entry.cover_url ? (
                                    <img src={entry.cover_url} alt={entry.title} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Video className="w-6 h-6 text-red-400/50" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-heading font-bold uppercase truncate">
                                    {entry.title || entry.target_id}
                                  </h4>
                                  {entry.handle && (
                                    <p className="text-xs text-muted-foreground">{entry.handle ? `@${entry.handle}` : ""}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
                                  <span className="flex items-center gap-1" title="Views">
                                    <Play className="w-3 h-3" /> {entry.stats?.views || 0}
                                  </span>
                                  <Badge variant="outline" className="text-xs text-red-400 border-red-400/30">
                                    {entry.score?.toFixed(1)}
                                  </Badge>
                                </div>
                              </div>
                            </Card>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state when no trending data */}
                {(!trendingData.top_tracks?.length && !trendingData.top_videos?.length) && (
                  <StageEmptyState
                    variant="charts"
                    customHeadline="Trending Charts Building Up"
                    customHint="As users play, like, and share content, trending rankings will automatically populate here."
                  />
                )}

                {/* Trending Now — 24h engagement spike */}
                {trendingNow.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-400" />
                      Trending Now <Badge className="bg-orange-500/20 text-orange-400 text-xs">24h</Badge>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="trending-now-section">
                      {trendingNow.slice(0, 8).map((item, i) => (
                        <Link key={item.target_id} to={item.link_url || `/${item.target_type}/${item.target_id}`}>
                          <Card className="glass-card border-border p-3 hover-lift">
                            <div className="flex items-center gap-3">
                              <span className="font-heading font-black text-lg text-orange-400 w-8 text-center">{item.rank}</span>
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                                {item.cover_url ? <img src={item.cover_url} alt="" className="w-full h-full object-cover" /> : <Flame className="w-5 h-5 text-orange-400/50" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm truncate">{item.title || item.target_id}</h4>
                                <p className="text-xs text-muted-foreground">{item.target_type} {item.handle ? `@${item.handle}` : ''}</p>
                              </div>
                              <Badge variant="outline" className="text-xs text-orange-400 border-orange-400/30">{item.score_24h?.toFixed(1)}</Badge>
                            </div>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rising Fast — velocity spike */}
                {risingFast.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                      Rising Fast <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Velocity</Badge>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="rising-fast-section">
                      {risingFast.slice(0, 8).map((item, i) => (
                        <Link key={item.target_id} to={item.link_url || `/${item.target_type}/${item.target_id}`}>
                          <Card className="glass-card border-border p-3 hover-lift">
                            <div className="flex items-center gap-3">
                              <span className="font-heading font-black text-lg text-emerald-400 w-8 text-center">{item.rank}</span>
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                                {item.cover_url ? <img src={item.cover_url} alt="" className="w-full h-full object-cover" /> : <TrendingUp className="w-5 h-5 text-emerald-400/50" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm truncate">{item.title || item.target_id}</h4>
                                <p className="text-xs text-muted-foreground">{item.target_type} {item.handle ? `@${item.handle}` : ''}</p>
                              </div>
                              <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/30">{item.velocity}x</Badge>
                            </div>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* New & Buzzing */}
                {newBuzzing.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      New & Buzzing <Badge className="bg-purple-500/20 text-purple-400 text-xs">7 days</Badge>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="new-buzzing-section">
                      {newBuzzing.slice(0, 8).map((item, i) => (
                        <Link key={item.target_id} to={item.link_url || `/${item.target_type}/${item.target_id}`}>
                          <Card className="glass-card border-border p-3 hover-lift">
                            <div className="flex items-center gap-3">
                              <span className="font-heading font-black text-lg text-purple-400 w-8 text-center">{item.rank}</span>
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                                {item.cover_url ? <img src={item.cover_url} alt="" className="w-full h-full object-cover" /> : <Sparkles className="w-5 h-5 text-purple-400/50" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm truncate">{item.title || item.target_id}</h4>
                                <p className="text-xs text-muted-foreground">{item.target_type} {item.handle ? `@${item.handle}` : ''}</p>
                              </div>
                              <Badge variant="outline" className="text-xs text-purple-400 border-purple-400/30">{item.score_24h?.toFixed(1)}</Badge>
                            </div>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <StageEmptyState
                variant="charts"
                customHeadline="Loading Trending Data..."
                customHint="Engagement-based rankings are being calculated."
              />
            )}
          </TabsContent>

          {/* Top 20 Overall */}
          <TabsContent value="overall">
            <div className="mb-4 sm:mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-bold uppercase mb-1 sm:mb-2">Top 20 DMV Artists</h2>
              <p className="text-sm sm:text-base text-muted-foreground">The primary prestige chart - ranked by digital requests this week</p>
            </div>
            <div className="space-y-3 sm:space-y-4" data-testid="artists-chart">
              {artistCharts.length > 0 ? (
                artistCharts.map((entry, index) => (
                  <ArtistChartCard key={entry.artist_id} entry={entry} index={index} />
                ))
              ) : (
                <EmptyState
                  icon={Trophy}
                  title="Charts Coming Soon"
                  description="Charts will populate as artists receive requests from listeners. Be the first to request your favorite DMV artists!"
                />
              )}
            </div>
          </TabsContent>

          {/* Videos Chart */}
          <TabsContent value="videos">
            <div className="mb-4 sm:mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-bold uppercase mb-1 sm:mb-2">Top 20 Music Videos</h2>
              <p className="text-sm sm:text-base text-muted-foreground">Hottest music videos in the DMV this week</p>
            </div>
            <div className="space-y-3 sm:space-y-4" data-testid="videos-chart">
              {videoCharts.length > 0 ? (
                videoCharts.map((entry, index) => (
                  <VideoChartCard key={entry.video_id} entry={entry} index={index} />
                ))
              ) : (
                <EmptyState
                  icon={Video}
                  title="Video Charts Coming Soon"
                  description="Video charts will populate as music videos receive requests. Support DMV artists by requesting their videos!"
                  color="secondary"
                />
              )}
            </div>
          </TabsContent>

          {/* Genre Charts */}
          <TabsContent value="genres">
            <div className="mb-4 sm:mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-bold uppercase mb-1 sm:mb-2">Genre Charts</h2>
              <p className="text-sm sm:text-base text-muted-foreground">Top artists by genre - charts activate with 5+ artists or 50+ requests</p>
            </div>
            
            {/* Genre Selection */}
            <div className="flex flex-wrap gap-2 mb-8">
              {genreCharts.map((genre) => (
                <Button
                  key={genre.genre_id}
                  variant={selectedGenre === genre.genre_id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGenre(genre.genre_id)}
                  disabled={!genre.is_active}
                  className={!genre.is_active ? "opacity-50" : ""}
                >
                  {genre.genre_name}
                  {genre.is_active && (
                    <Badge className="ml-2 bg-secondary/20 text-secondary text-xs">
                      {genre.total_requests}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>

            {/* Selected Genre Chart */}
            {selectedGenre && genreData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold">
                    Top 15 {genreData.chart_info?.genre_name}
                  </h3>
                  <Badge variant="outline">This Week</Badge>
                </div>
                {genreData.entries?.length > 0 ? (
                  genreData.entries.map((entry, index) => (
                    <ArtistChartCard key={entry.artist_id} entry={entry} index={index} showBadges={false} />
                  ))
                ) : (
                  <EmptyState
                    icon={Music}
                    title="No Chart Data Yet"
                    description={`The ${genreData.chart_info?.genre_name} chart will populate as artists receive requests.`}
                  />
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a genre above to view its chart</p>
              </div>
            )}
          </TabsContent>

          {/* Regional Charts */}
          <TabsContent value="regions">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-2">Regional Charts</h2>
              <p className="text-muted-foreground">Top 10 artists by region - DC, Maryland, Virginia</p>
            </div>
            
            {/* Region Selection */}
            <div className="flex flex-wrap gap-2 mb-8">
              {regionalCharts.map((region) => (
                <Button
                  key={region.region_id}
                  variant={selectedRegion === region.region_id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedRegion(region.region_id)}
                  disabled={!region.is_active}
                  className={!region.is_active ? "opacity-50" : ""}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {region.region_name}
                  {region.is_active && (
                    <Badge className="ml-2 bg-secondary/20 text-secondary text-xs">
                      {region.artist_count} artists
                    </Badge>
                  )}
                </Button>
              ))}
            </div>

            {/* Selected Region Chart */}
            {selectedRegion && regionData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold">
                    Top 10 {regionData.chart_info?.region_name} Artists
                  </h3>
                  <Badge variant="outline">This Week</Badge>
                </div>
                {regionData.entries?.length > 0 ? (
                  regionData.entries.map((entry, index) => (
                    <ArtistChartCard key={entry.artist_id} entry={entry} index={index} showBadges={false} />
                  ))
                ) : (
                  <EmptyState
                    icon={MapPin}
                    title="No Chart Data Yet"
                    description={`The ${regionData.chart_info?.region_name} chart will populate as artists from the region receive requests.`}
                  />
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a region above to view its chart</p>
              </div>
            )}
          </TabsContent>

          {/* Rising Artists */}
          <TabsContent value="rising">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-2">Rising Artists</h2>
              <p className="text-muted-foreground">Top 10 artists in their first 30 days on the platform</p>
            </div>
            <div className="space-y-4" data-testid="rising-chart">
              {risingArtists.length > 0 ? (
                risingArtists.map((entry, index) => (
                  <ArtistChartCard key={entry.artist_id} entry={entry} index={index} />
                ))
              ) : (
                <EmptyState
                  icon={Sparkles}
                  title="Rising Artists Coming Soon"
                  description="This chart highlights new artists in their first 30 days. Check back as more artists join the platform!"
                />
              )}
            </div>
          </TabsContent>

          {/* New Releases */}
          <TabsContent value="new-releases">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-2">New Releases</h2>
              <p className="text-muted-foreground">Top 10 tracks released in the last 14 days</p>
            </div>
            <div className="space-y-4" data-testid="new-releases-chart">
              {newReleases.length > 0 ? (
                newReleases.map((entry, index) => (
                  <motion.div
                    key={entry.track_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Card className={`glass-card border-border p-4 sm:p-6 hover-lift ${entry.rank <= 3 ? 'neon-border' : ''}`}>
                      <div className="flex items-center gap-4 sm:gap-6">
                        {/* Rank */}
                        <div className="w-12 sm:w-16 text-center">
                          <span className={`font-heading font-black ${getRankStyle(entry.rank)}`}>
                            {entry.rank}
                          </span>
                        </div>

                        {/* Cover */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                          {entry.cover_url ? (
                            <img src={entry.cover_url} alt={entry.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <Music className="w-8 h-8" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading text-xl font-bold uppercase truncate">
                              {entry.title}
                            </h3>
                            {getRankBadge(entry.rank)}
                            <Badge className="bg-green-500/20 text-green-400 text-xs">NEW</Badge>
                          </div>
                          <Link to={`/artist/${entry.artist_id}`} className="text-sm text-muted-foreground hover:text-secondary">
                            {entry.artist_name}
                          </Link>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-4 h-4 text-secondary" />
                              {entry.total_requests} requests
                            </span>
                            {entry.genre && (
                              <span className="flex items-center gap-1">
                                <Music className="w-4 h-4" />
                                {entry.genre}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <EmptyState
                  icon={Clock}
                  title="New Releases Coming Soon"
                  description="This chart highlights the hottest new tracks from the last 14 days."
                />
              )}
            </div>
          </TabsContent>

          {/* Creator Role Charts */}
          {CREATOR_CHART_TABS.map(tab => (
            <TabsContent key={tab.id} value={tab.id}>
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-bold uppercase mb-2">{tab.name}</h2>
                <p className="text-muted-foreground">
                  {creatorCharts[tab.id]?.description || `Top ${tab.name.toLowerCase()} in the DMV this week`}
                </p>
              </div>
              <div className="space-y-4" data-testid={`${tab.id}-chart`}>
                {creatorCharts[tab.id]?.chart?.length > 0 ? (
                  creatorCharts[tab.id].chart.map((entry, index) => (
                    <CreatorChartCard key={entry.user_id} entry={entry} index={index} role={tab.id} />
                  ))
                ) : (
                  <EmptyState
                    icon={tab.icon}
                    title={`${tab.name} Charts Coming Soon`}
                    description={`Charts will populate as ${tab.name.toLowerCase()} receive requests and engagement.`}
                  />
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
